import React from 'react'
import {
    Box,
    Text,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    Button,
    SimpleGrid,
    useStyleConfig,
    VStack,
    HStack,
    useDisclosure,
    Flex,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import useCountries from '../../hooks/use-countries'
import ReactCountryFlag from 'react-country-flag'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {CloseIcon, WebsiteIcon, CloseLabelIcon} from '../icons'
import {useGetCountrySelector} from '../../custom-api/hooks/use-get-country-selector'
import {absoluteUrl} from '../../utils/url'
import useCurrentHost from '../../hooks/use-current-host'
import {BRAND_KEY} from '../../brand'
import {FormattedMessage, useIntl} from 'react-intl'

export const Country = ({locales, countryCode, isAbsoluteUrl = false}) => {
    const styles = useStyleConfig('CountrySelectorFooter')
    const folderFlags = getAssetUrl('static/img/flags/4x3/')
    const {getHost} = useCurrentHost()
    const origin = getHost()
    const intl = useIntl()

    return (
        <>
            {locales.map((locale) => {
                const url = isAbsoluteUrl ? absoluteUrl(locale?.url, origin) : locale?.url
                return (
                    <Button
                        as="a"
                        href={url}
                        target={isAbsoluteUrl ? '_blank' : undefined}
                        rel={isAbsoluteUrl ? 'noopener noreferrer' : undefined}
                        variant="ghost-not-rounded"
                        key={`${countryCode}-${locale.lang}`}
                        {...styles?.countryButton}
                    >
                        <Flex {...styles?.countryContainer}>
                            <Flex {...styles?.countrySvgContainer}>
                                <ReactCountryFlag
                                    countryCode={countryCode}
                                    svg
                                    cdnUrl={folderFlags}
                                    cdnSuffix="svg"
                                    alt={intl.formatMessage({
                                        id: 'footer.country_selector_currently_selected_country_icon',
                                        defaultMessage:
                                            'A small icon representing the currently selected country',
                                    })}
                                    style={{
                                        width: 'auto',
                                        height: '100%',
                                        objectFit: 'cover',
                                        objectPosition: 'center',
                                    }}
                                />
                            </Flex>
                        </Flex>

                        <VStack {...styles?.countryVStack}>
                            <Text {...styles?.textCountry}>{locale?.country}</Text>

                            <Text {...styles?.textCode}>{locale?.lang}</Text>
                        </VStack>
                    </Button>
                )
            })}
        </>
    )
}

Country.propTypes = {
    locales: PropTypes.array.isRequired,
    countryCode: PropTypes.string.isRequired,
    intl: PropTypes.object,
    isAbsoluteUrl: PropTypes.bool,
}

export const SelectorModal = ({isOpen, onClose, intl}) => {
    const styles = useStyleConfig('CountrySelectorFooter')
    const {data} = useGetCountrySelector()
    const countries = useCountries(data)

    const props = {
        'data-testid': 'close-button',
        ...styles?.closeIcon,
        onClick: onClose,
        onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                onClose()
            }
        },
        'aria-label': intl.formatMessage({
            id: 'country.banner.close',
            defaultMessage: 'Close',
        }),
        tabIndex: 0,
    }
    const CloseCountrySelectorIcon =
        BRAND_KEY === 'braun' ? (
            <CloseLabelIcon fill="icons.background.secondary" cursor="pointer" {...props} />
        ) : (
            <CloseIcon {...props} />
        )

    return (
        <Modal
            {...styles?.modalContainer}
            isOpen={isOpen}
            onClose={onClose}
            blockScrollOnMount={false}
        >
            <ModalOverlay />
            <ModalContent
                {...styles?.selectorContent}
                containerProps={{justifyContent: 'flex-end'}}
            >
                <ModalHeader {...styles?.selectorHeader}>
                    <Flex {...styles?.headerTextContainer}>
                        <Text {...styles?.headerText}>{data?.selector_title}</Text>

                        <Flex {...styles?.headerSubtitleContainer}>
                            <Text {...styles?.headerSubtitle}>
                                <FormattedMessage
                                    id="country.selector.subtitle"
                                    defaultMessage="Select from the lists below or visit our {international_website}"
                                    values={{
                                        international_website: (
                                            <Button
                                                as="a"
                                                variant="link-button-no-underline"
                                                size="base"
                                                href={data?.international_cta?.href}
                                                {...styles?.headerSubtitleButton}
                                                data-testid="international-website-cta"
                                            >
                                                <FormattedMessage
                                                    id="country.selector.link"
                                                    defaultMessage="international website"
                                                />
                                            </Button>
                                        ),
                                    }}
                                />
                            </Text>
                        </Flex>
                    </Flex>

                    {CloseCountrySelectorIcon}
                </ModalHeader>

                <ModalBody data-testid="modal-body" {...styles?.modalBody}>
                    {countries.map(
                        (dataSet, idx) =>
                            Object.keys(dataSet.countries).length > 0 && (
                                <Box {...styles?.region} key={idx}>
                                    <Text {...styles?.textHeader}>{dataSet.region}</Text>

                                    <SimpleGrid {...styles?.selectorGrid}>
                                        {Object.entries(dataSet.countries).map(
                                            ([countryCode, locales]) => (
                                                <Country
                                                    key={countryCode}
                                                    locales={locales}
                                                    countryCode={countryCode}
                                                    intl={intl}
                                                />
                                            ),
                                        )}
                                    </SimpleGrid>
                                </Box>
                            ),
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

SelectorModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    intl: PropTypes.object,
}

const CountrySelector = ({intl}) => {
    const styles = useStyleConfig('CountrySelectorFooter')
    const {isOpen, onOpen, onClose} = useDisclosure()
    const folderFlags = getAssetUrl('static/img/flags/4x3/')

    const selectedLocale = intl.locale.split('-')[1]?.toUpperCase()

    const countryName = selectedLocale
        ? intl.formatDisplayName(selectedLocale, {type: 'region'})
        : 'International'

    return (
        <>
            <Button
                data-testid="country-button"
                {...styles?.openButton}
                onClick={onOpen}
                tabIndex={0}
                aria-label={intl.formatMessage({
                    id: 'footer.country_selector.open_aria_label',
                    defaultMessage: 'Open country selector',
                })}
            >
                <HStack {...styles?.selectorSubContainer}>
                    {selectedLocale ? (
                        <Flex {...styles?.countrySvgContainer}>
                            <ReactCountryFlag
                                countryCode={selectedLocale}
                                svg
                                cdnUrl={folderFlags}
                                cdnSuffix="svg"
                                alt={intl.formatMessage({
                                    id: 'footer.country_selector_currently_selected_country_icon',
                                    defaultMessage:
                                        'A small icon representing the currently selected country',
                                })}
                                style={{
                                    width: 'auto',
                                    height: '100%',
                                    objectFit: 'cover',
                                    objectPosition: 'center',
                                }}
                            />
                        </Flex>
                    ) : (
                        <WebsiteIcon {...styles.websiteIcon} />
                    )}

                    <Text {...styles?.buttonText}>{countryName}</Text>
                </HStack>
            </Button>
            <SelectorModal isOpen={isOpen} onClose={onClose} intl={intl} />
        </>
    )
}

CountrySelector.propTypes = {
    intl: PropTypes.object.isRequired,
}

export default CountrySelector
